import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n/languages';
import { initialsOf } from '@/pages/public/booking-page/helpers';
import type { BookingPageAdmin } from '../api';

const TAGLINE_MAX = 80;

export function HeaderCard({ organization, tagline, onTagline }: {
  organization: BookingPageAdmin['organization'];
  tagline: string;
  onTagline: (value: string) => void;
}) {
  const { t } = useTranslation('adminBookingPage');
  const languages = SUPPORTED_LANGUAGES.filter((l) => organization.public_locales.includes(l.code)).map((l) => l.name).join(', ');

  return (
    <section className="rounded-2xl border border-line bg-surface shadow-card" data-testid="header-card">
      <div className="flex flex-col gap-1 px-4 pt-4 sm:px-[22px] sm:pt-5">
        <h2 className="font-display text-[17px] font-semibold tracking-tight text-ink sm:text-[18px]">{t('header.title')}</h2>
        <p className="text-body text-soft">{t('header.description')}</p>
      </div>
      <div className="flex flex-col gap-[18px] p-4 sm:px-[22px] sm:pb-[22px]">
        <div className="flex items-center gap-3.5 rounded-xl border border-line bg-bg-public px-3.5 py-3">
          {organization.logo_url ? (
            <img src={organization.logo_url} alt="" className="h-11 w-11 rounded-lg border border-line bg-surface object-contain p-1" />
          ) : (
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-100 font-display text-xl font-semibold text-brand-700">
              {initialsOf(organization.name).slice(0, 1)}
            </span>
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-body font-semibold text-ink">{organization.name}</span>
            {!organization.logo_url && <span className="text-body-sm text-soft">{t('header.noLogo')}</span>}
          </div>
          <Link href="/admin/settings?tab=profile" className="hidden items-center gap-1.5 text-body font-medium text-brand-500 hover:text-brand-700 sm:inline-flex">
            {t('header.editProfile')}
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>

        <label className="flex flex-col gap-1.5" htmlFor="tagline">
          <span className="text-label font-semibold text-ink">{t('header.tagline')}</span>
          <Input
            id="tagline"
            value={tagline}
            maxLength={TAGLINE_MAX}
            placeholder={t('header.taglinePlaceholder')}
            onChange={(e) => onTagline(e.target.value)}
            className="h-11"
          />
          <span className="flex justify-between text-body-xs text-soft">
            <span>{t('header.taglineHint')}</span>
            <span className="tabular-nums">{tagline.length} / {TAGLINE_MAX}</span>
          </span>
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-label font-semibold text-ink">{t('header.about')}</span>
          <div className="rounded-md border border-line bg-bg-public px-3.5 py-3 text-body text-soft line-clamp-3" data-testid="about-preview">
            {organization.description || t('header.aboutEmpty')}
          </div>
          <span className="flex justify-between gap-3 text-body-xs text-soft">
            <span>{t('header.aboutHint')}</span>
            <Link href="/admin/settings?tab=profile" className="font-medium text-brand-500">{t('header.edit')}</Link>
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-body">
          <span className="text-soft">{t('header.languages')}: <span className="font-medium text-ink">{languages}</span></span>
        </div>
      </div>
    </section>
  );
}
